import { useEffect, useRef } from "react";
import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosRequestHeaders,
  InternalAxiosRequestConfig,
} from "axios";
import { useSelector } from "react-redux";
import { conf } from "../utils/config";
import {
  selectAuthenticated,
  selectToken,
  setAuthtenticated,
  setToken,
} from "../features/Dashboard/Slices/userSlice";
import { useAppDispatch } from "../app/store";
import { useNavigate } from "react-router-dom";
import { selectTokenValidationStatus } from "../features/Dashboard/Slices/dashboardSlice";
import { validateToken } from "../features/Dashboard/AsyncThunks/estadoGeneral/validateToken";
import { isFulfilled } from "@reduxjs/toolkit";
import useAuth from "../hooks/useAuth";

export enum ServerStatus {
  Success = 200,
  BadRequest = 400,
  Unauthorized = 401,
  InternalServerError = 500,
}

function AxiosManager() {
  const token = useSelector(selectToken);
  const { logout } = useAuth();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const authenticated = useSelector(selectAuthenticated);
  const tokenValidationStatus = useSelector(selectTokenValidationStatus);
  const defaultConfig: AxiosRequestConfig = {
    baseURL: conf("BACKEND_API_DASHBOARD_URL") || conf("BACKEND_API_URL"),
  };
  const fallbackBaseURL = conf("BACKEND_API_URL");
  const responseInterceptorId = useRef<number | null>(null);
  const requestInterceptorId = useRef<number | null>(null);

  const handleLogout = async () => {
    logout();
  };

  const checkToken = async () => {
    const tkn = localStorage.getItem("token");
    if (tkn) {
      dispatch(setToken(tkn));
      const authenticatedResult = await dispatch(validateToken(tkn));
      if (isFulfilled(authenticatedResult)) {
        dispatch(setAuthtenticated(true));
        navigate("/home");
      } else {
        handleLogout();
      }
    }
  };

  useEffect(() => {
    checkToken();
  }, []);

  useEffect(() => {
    // Eliminar interceptor anterior si existe
    if (responseInterceptorId.current !== null) {
      axios.interceptors.response.eject(responseInterceptorId.current);
    }
    if (requestInterceptorId.current !== null) {
      axios.interceptors.request.eject(requestInterceptorId.current);
    }

    // Configurar interceptor de respuesta con fallback (siempre, no solo cuando está autenticado)
    responseInterceptorId.current = axios.interceptors.response.use(
      (response) => {
        if (authenticated && response.status === ServerStatus.Unauthorized) {
          handleLogout();
        }
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean; _fallbackUsed?: boolean };
        
        // Verificar si esta petición proviene de una instancia personalizada
        const isCustomInstance = (originalRequest as any).__isCustomInstance || 
                                 (originalRequest?.adapter && (originalRequest.adapter as any).__isCustomInstance);
        
        // Solo aplicar fallback para peticiones que no sean de instancias personalizadas
        if (!isCustomInstance && originalRequest && !originalRequest._retry && !originalRequest._fallbackUsed) {
          // Verificar si el baseURL actual es el dashboard y existe un fallback disponible
          const currentBaseURL = originalRequest.baseURL || defaultConfig.baseURL;
          const dashboardURL = conf("BACKEND_API_DASHBOARD_URL");
          let isUsingDashboardURL = false;
          
          if (dashboardURL && currentBaseURL) {
            try {
              const dashboardHost = new URL(dashboardURL).hostname;
              const currentHost = new URL(currentBaseURL).hostname;
              isUsingDashboardURL = currentBaseURL === dashboardURL || currentHost === dashboardHost;
            } catch (e) {
              // Si no es una URL válida, hacer comparación directa
              isUsingDashboardURL = currentBaseURL === dashboardURL;
            }
          }
          
          // Verificar si el error debe activar el fallback (404, errores de red, timeout, o 5xx)
          const shouldRetry = 
            !error.response || // Error de red (sin respuesta)
            error.code === 'ECONNABORTED' || // Timeout
            error.code === 'ERR_NETWORK' || // Error de red
            error.response?.status === 404 || // Not Found - el endpoint no existe en el dashboard
            (error.response?.status && error.response.status >= 500 && error.response.status < 600); // Errores 5xx
          
          if (shouldRetry && isUsingDashboardURL && fallbackBaseURL) {
            console.log('[AxiosManager Fallback] Error detectado, cambiando de', currentBaseURL, 'a', fallbackBaseURL);
            originalRequest._retry = true;
            originalRequest._fallbackUsed = true;
            
            // Cambiar el baseURL al fallback
            originalRequest.baseURL = fallbackBaseURL;
            
            // Reintentar la petición con el nuevo baseURL
            return axios(originalRequest);
          }
        }
        
        if (authenticated && error.response?.status === ServerStatus.Unauthorized) {
          handleLogout();
        }
        return Promise.reject(error);
      }
    );
    
    // Configurar interceptor de request
    requestInterceptorId.current = axios.interceptors.request.use(
      (config) => {
        // Verificar si esta petición proviene de una instancia personalizada
        // Si es así, NO modificar el baseURL en absoluto
        const isCustomInstance = (config as any).__isCustomInstance || 
                                 (config.adapter && (config.adapter as any).__isCustomInstance);
        
        // Si es una instancia personalizada, NO tocar el baseURL (incluso si es undefined)
        // Solo establecer baseURL si NO es una instancia personalizada Y no está configurado
        if (!isCustomInstance && !config.baseURL) {
          config.baseURL = defaultConfig.baseURL;
        }
        
        const tkn = localStorage.getItem("token");
        const newToken = token ? token : tkn;
        if (newToken) {
          config.headers = {
            ...config.headers,
            Authorization: "Bearer " + newToken,
            Accept: "application/json",
            "Content-Type": "application/json",
          } as AxiosRequestHeaders;
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Cleanup: eliminar interceptores al desmontar o cuando cambien las dependencias
    return () => {
      if (responseInterceptorId.current !== null) {
        axios.interceptors.response.eject(responseInterceptorId.current);
        responseInterceptorId.current = null;
      }
      if (requestInterceptorId.current !== null) {
        axios.interceptors.request.eject(requestInterceptorId.current);
        requestInterceptorId.current = null;
      }
    };
  }, [token, authenticated]);

  useEffect(() => {
    if (tokenValidationStatus === "invalid") {
      handleLogout();
    }
  }, [tokenValidationStatus]);

  return <></>;
}

export default AxiosManager;
